
import React from 'react';
import { Helmet } from 'react-helmet';

const FraudAPIPage = () => {
  return (
    <div className="bg-secondary text-text-light py-24">
      <Helmet>
        <title>Developer API for Fraud Detection | Aletheia Systems</title>
        <meta name="description" content="Integrate our powerful fraud detection API into your applications. Get started with our developer documentation and API reference." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-extrabold text-center mb-12 text-white">API Documentation: Customer Usage Guide</h1>
        <div className="prose prose-invert max-w-none">
          <h2 className="text-white">Overview</h2>
          <p>This document provides a step-by-step guide for interacting with the API provided by Aletheia Systems. The API enables customers to validate user admissions information via HTTP requests. This guide includes details on authentication, request formats, and examples for validating single or multiple records.</p>

          <div className="my-8 border-t border-gray-700"></div>

          <h2 className="text-white">Authentication</h2>
          <p>To access the API, you must first obtain a set of tokens (Access Token, Refresh Token, and Id Token). These tokens are used to authenticate subsequent API requests.</p>

          <h3 className="text-white">Endpoint:</h3>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">https://api.aletheiasystems.com/login</pre>
          </div>

          <h3 className="text-white">Request:</h3>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">
              <code className="text-accent-cyan">curl</code> -u <code className="text-primary">'username:password'</code> https://api.aletheiasystems.com/login
            </pre>
          </div>

          <h3 className="text-white">Example Response:</h3>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">{`{
  "AccessToken": "<redacted_access_token>",
  "ExpiresIn": 3600,
  "TokenType": "Bearer",
  "RefreshToken": "<redacted_refresh_token>",
  "IdToken": "<redacted_id_token>"
}`}</pre>
          </div>

          <div className="my-8 border-t border-gray-700"></div>

          <h2 className="text-white">Validating User Admissions Information</h2>
          <p>Once authenticated, use the access token to validate user admissions information. The API supports two validation methods:</p>
          <ol>
            <li>Single Record Validation (via GET)</li>
            <li>Multiple Records Validation (via POST)</li>
          </ol>

          <h3 className="text-white">Input Parameters</h3>
          <p>The following fields can be included in validation requests:</p>
          <ul>
            <li><code>fname</code> (First Name)</li>
            <li><code>lname</code> (Last Name)</li>
            <li><code>mname</code> (Middle Name)</li>
            <li><code>email</code> (Email Address)</li>
            <li><code>phone</code> (Phone Number)</li>
            <li><code>addressline1</code>, <code>addressline2</code>, <code>addressline3</code>, <code>addressline4</code></li>
            <li><code>country</code></li>
            <li><code>city</code></li>
            <li><code>state</code></li>
            <li><code>postalcode</code></li>
            <li><code>targetpostalcode</code> (Optional: Used for distance comparisons to specific campuses. Defaults to <code>postalcode</code> if not provided)</li>
            <li><code>ip</code> (IP Address)</li>
          </ul>

          <h3 className="text-white">1. Single Record Validation</h3>
          <p>Use the GET method to validate a single record by providing the required parameters in the query string.</p>

          <h4 className="text-white">Endpoint:</h4>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">https://api.aletheiasystems.com/validate</pre>
          </div>

          <h4 className="text-white">Example Request:</h4>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light"><code>{`curl -H "Authorization: Bearer \${AccessToken}" 'https://api.aletheiasystems.com/validate?addressline1=123+some+street&city=Miami&state=fl&postalcode=&email=&fname=Fred&lname=Flintstone&phone=&ssn='`}</code></pre>
          </div>

          <h4 className="text-white">Example Response:</h4>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">{`{
  "status": [
    {"code": "CAE02", "short": "Unknown Street", "long": "Could not match the input street to a unique street name. Either no matches or too many matches found."},
    {"code": "CAS81", "short": "Non Residential Address", "long": "The address is a business or unknown address type"}
  ],
  "nascore": 14,
  "adscore": 17,
  "score": 15
}`}</pre>
          </div>

          <h3 className="text-white">2. Multiple Records Validation</h3>
          <p>Use the POST method to validate multiple records. Each record should include a <code>recordid</code> field, which will be returned in the response for easier tracking.</p>

          <h4 className="text-white">Endpoint:</h4>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">https://api.aletheiasystems.com/validate</pre>
          </div>

          <h4 className="text-white">Example Request:</h4>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light"><code>{`curl -H "Content-Type: application/json" -H "Authorization: Bearer \\\${AccessToken}" -X POST -d '{"Records": [
  {
    "recordid": 112,
    "city": "State University",
    "name": "Accounts Payable/Disbursing",
    "addressline2": "",
    "company": "Arkansas State University",
    "addressline1": "PO Box 850",
    "state": "AR"
  },
  {
    "recordid": 113,
    "city": "Russellville",
    "name": "Accounts Payable",
    "addressline2": "",
    "company": "Arkansas Tech University",
    "addressline1": "1505 N Boulder Ave Rm 302",
    "state": "AR"
  }
]}' 'https://api.aletheiasystems.com/validate'`}</code></pre>
          </div>

          <h4 className="text-white">Example Response:</h4>
          <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm mb-4">
            <pre className="text-text-light">{`{
"Results": [
  {
    "status": [
      {"code": "CAC01", "short": "Postal Code Change", "long": "The postal code was changed or added."},
      {"code": "CAS01", "short": "Address Fully Verified", "long": "The address is valid and deliverable according to official postal agencies."},
      {"code": "CAS20", "short": "Deliverable Only by USPS", "long": "US Only. This address can only receive mail delivered through the USPS (i.e. PO Box or a military address)."},
      {"code": "CAS81", "short": "Non Residential Address", "long": "The address is a business or unknown address type"}
    ],
    "adscore": 31,
    "score": 31,
    "recordid": 112
  },
  {
    "status": [
      {"code": "CAC01", "short": "Postal Code Change", "long": "The postal code was changed or added."},
      {"code": "CAS01", "short": "Address Fully Verified", "long": "The address is valid and deliverable according to official postal agencies."},
      {"code": "CAS17", "short": "No USPS Mail Delivery", "long": "This address is classified as not receiving mail by the USPS via their No-Stat flag. Delivery might still be possible by third party carriers but that cannot be counted on."},
      {"code": "CAS23", "short": "Extraneous Suite Information", "long": "Extraneous information not used in verifying the address was found. This information was returned in the suite field."},
      {"code": "CAS81", "short": "Non Residential Address", "long": "The address is a business or unknown address type"}
    ],
    "adscore": 39,
    "score": 39,
    "recordid": 113
  }
]
}`}</pre>
          </div>

          <div className="my-8 border-t border-gray-700"></div>

          <h2 className="text-white">Additional Notes</h2>
          <ul>
            <li>Ensure all requests include the Authorization header with a valid access token.</li>
            <li>Response details, such as status codes and their meanings, will be covered in a separate document.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FraudAPIPage;
